import React, { useState, useEffect } from 'react';
import MobileQRScanner from './MobileQRScanner';
import MobileQRGenerator from './MobileQRGenerator';
import ComprehensiveTimesheet from './ComprehensiveTimesheet';
import '../styles/MobileTimesheetApp.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MobileTimesheetApp = () => {
  const [activeTab, setActiveTab] = useState('scanner');
  const [scanResult, setScanResult] = useState('');
  const [workOrderData, setWorkOrderData] = useState(null);
  const [timesheetData, setTimesheetData] = useState({
    // Basic Info
    employee_name: '',
    week_ending: new Date().toISOString().split('T')[0],
    work_order_number: '',
    sub_number: '',
    customer_name: '',
    job_site: '',
    customer_po_number: '',
    activity_covered: '',
    
    // Time Tracking (Daily Hours)
    regular_hours: { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 },
    overtime_hours: { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 },
    premium_hours: { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 },
    travel_hours: { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 },
    
    // Labour Codes and Rates
    labour_code: '',
    labour_rate: 0,
    
    // Equipment Usage
    equipment_used: [
      { name: 'Service Vehicle', qty: 0, days: 0, rate: 0, subtotal: 0 },
      { name: 'Trailer', qty: 0, days: 0, rate: 0, subtotal: 0 },
      { name: 'Crane _____ ton', qty: 0, days: 0, rate: 0, subtotal: 0 },
      { name: 'Forklift _____ ton', qty: 0, days: 0, rate: 0, subtotal: 0 },
      { name: 'Broderson _____ ton', qty: 0, days: 0, rate: 0, subtotal: 0 },
      { name: 'Articulating Lift', qty: 0, days: 0, rate: 0, subtotal: 0 },
      { name: 'Scissor Lift', qty: 0, days: 0, rate: 0, subtotal: 0 }
    ],
    
    // Consumables
    consumables_used: [
      // Gases
      { name: 'Propane (33lbs)', qty: 0, rate: 25.00, subtotal: 0 },
      { name: 'Oxygen', qty: 0, rate: 30.00, subtotal: 0 },
      { name: 'Acetylene', qty: 0, rate: 35.00, subtotal: 0 },
      { name: 'Argon (Small)', qty: 0, rate: 40.00, subtotal: 0 },
      { name: 'Argon (Large)', qty: 0, rate: 60.00, subtotal: 0 },
      { name: 'Nitrogen', qty: 0, rate: 35.00, subtotal: 0 },
      
      // Fuel & Transportation
      { name: 'Fuel Surcharge', qty: 0, rate: 15.00, subtotal: 0 },
      { name: '407 ETR', qty: 0, rate: 0, subtotal: 0 },
      { name: 'Diesel Fuel', qty: 0, rate: 0, subtotal: 0 },
      { name: 'Gasoline', qty: 0, rate: 0, subtotal: 0 },
      
      // Welding Rods & Materials
      { name: '7018 x 1/8" (lbs)', qty: 0, rate: 8.00, subtotal: 0 },
      { name: '7018 x 3/32" (lbs)', qty: 0, rate: 8.00, subtotal: 0 },
      { name: '7018 x 5/32" (lbs)', qty: 0, rate: 8.00, subtotal: 0 },
      { name: 'Other Welding Rod', qty: 0, rate: 8.00, subtotal: 0 },
      
      // Cutting & Grinding Supplies
      { name: '5" Zipcut Discs', qty: 0, rate: 2.50, subtotal: 0 },
      { name: '6" Zipcut Discs', qty: 0, rate: 3.00, subtotal: 0 },
      { name: '5" Grinding Discs', qty: 0, rate: 2.00, subtotal: 0 },
      { name: '6" Grinding Discs', qty: 0, rate: 2.50, subtotal: 0 },
      { name: 'Sanding Discs', qty: 0, rate: 1.50, subtotal: 0 },
      { name: 'Polishing Discs', qty: 0, rate: 1.50, subtotal: 0 },
      { name: 'Deburr Bits', qty: 0, rate: 5.00, subtotal: 0 },
      
      // Safety Equipment
      { name: 'Welding Sleeves', qty: 0, rate: 20.00, subtotal: 0 },
      { name: 'Welding Gloves', qty: 0, rate: 15.00, subtotal: 0 },
      { name: 'TIG Gloves', qty: 0, rate: 18.00, subtotal: 0 },
      { name: 'Hand Heat Shields', qty: 0, rate: 12.00, subtotal: 0 },
      { name: 'Inside Lenses', qty: 0, rate: 3.00, subtotal: 0 },
      { name: 'Outside Lenses', qty: 0, rate: 2.00, subtotal: 0 },
      { name: '3M Organic Filters', qty: 0, rate: 8.00, subtotal: 0 },
      { name: 'Safety Glasses', qty: 0, rate: 5.00, subtotal: 0 },
      { name: 'Box Masks', qty: 0, rate: 25.00, subtotal: 0 },
      { name: 'Clear Face Shields', qty: 0, rate: 10.00, subtotal: 0 },
      
      // Hardware & Fasteners
      { name: 'Shim (4x4)', qty: 0, rate: 2.00, subtotal: 0 },
      { name: 'Anchors 3/8" x 4"', qty: 0, rate: 1.50, subtotal: 0 },
      { name: 'Anchors 1/2" x 5"', qty: 0, rate: 2.00, subtotal: 0 },
      { name: 'Chemical Anchors', qty: 0, rate: 5.00, subtotal: 0 },
      { name: 'Grout', qty: 0, rate: 0, subtotal: 0 },
      { name: 'Cutting Fluid', qty: 0, rate: 0, subtotal: 0 }
    ],
    
    // Purchases
    purchases: [],
    
    // Tools/Equipment (Yes/No tracking)
    tools_used: {
      company_vehicle: false,
      took_407: false,
      welder: false,
      trailer: false,
      cutting_torches: false,
      hammer_drill: false,
      track_jacks: false,
      skates: false,
      dollies: false,
      roto_hammer: false,
      hydraulic_rams: false,
      machine_skates: false,
      corner_dollies: false,
      magdrill: false,
      auto_level: false
    },
    
    // Signatures and Approval
    technician_signature: '',
    customer_signature: '',
    customer_approval_name: '',
    
    // Metadata
    created_at: new Date().toISOString(),
    offline_created: false,
    sync_status: 'pending'
  });
  const [notifications, setNotifications] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Monitor network status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const showNotification = (type, message) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, type, message }]);
    
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const handleScan = async (result) => {
    setScanResult(result);
    showNotification('success', 'QR code scanned successfully!');
    
    // Try to parse as work order QR code
    try {
      const parseResponse = await fetch(`${API}/mobile/timesheets/parse-qr`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ content: result })
      });

      if (parseResponse.ok) {
        const parseData = await parseResponse.json();
        if (parseData.success && parseData.work_order_number) {
          // Get work order details
          const workOrderResponse = await fetch(`${API}/mobile/timesheets/work-order/${parseData.work_order_number}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });

          if (workOrderResponse.ok) {
            const workOrderResult = await workOrderResponse.json();
            if (workOrderResult.success) {
              setWorkOrderData(workOrderResult.work_order);
              setTimesheetData(prev => ({
                ...prev,
                work_order_number: workOrderResult.work_order.work_order_number,
                employee_name: workOrderResult.work_order.assigned_technician || prev.employee_name
              }));
              setActiveTab('timesheet');
              showNotification('success', `Work order ${workOrderResult.work_order.work_order_number} loaded!`);
              return;
            }
          }
        }
      }
    } catch (error) {
      console.error('Error parsing QR code:', error);
    }

    // If not a work order QR, just show the raw result
    showNotification('info', 'QR code content captured');
  };

  const handleScanError = (error) => {
    showNotification('error', error);
  };

  const handleGenerate = (result) => {
    showNotification('success', 'QR code generated successfully!');
  };

  const handleTimesheetSubmit = async (submittedData) => {
    // Update local state
    setTimesheetData(submittedData);
    
    if (!isOnline) {
      // Store offline for later sync
      const offlineData = { ...submittedData, offline_created: true, sync_status: 'pending' };
      const offlineTimesheets = JSON.parse(localStorage.getItem('offlineTimesheets') || '[]');
      offlineTimesheets.push(offlineData);
      localStorage.setItem('offlineTimesheets', JSON.stringify(offlineTimesheets));
      
      showNotification('info', 'Timesheet saved offline. Will sync when online.');
      return;
    }

    try {
      const response = await fetch(`${API}/mobile/timesheets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(submittedData)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          showNotification('success', 'Timesheet submitted successfully!');
          // Reset form to initial state
          setTimesheetData({
            // Basic Info
            employee_name: '',
            week_ending: new Date().toISOString().split('T')[0],
            work_order_number: '',
            sub_number: '',
            customer_name: '',
            job_site: '',
            customer_po_number: '',
            activity_covered: '',
            
            // Time Tracking (Daily Hours)
            regular_hours: { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 },
            overtime_hours: { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 },
            premium_hours: { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 },
            travel_hours: { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 },
            
            // Labour Codes and Rates
            labour_code: '',
            labour_rate: 0,
            
            // Equipment Usage
            equipment_used: [
              { name: 'Service Vehicle', qty: 0, days: 0, rate: 0, subtotal: 0 },
              { name: 'Trailer', qty: 0, days: 0, rate: 0, subtotal: 0 },
              { name: 'Crane _____ ton', qty: 0, days: 0, rate: 0, subtotal: 0 },
              { name: 'Forklift _____ ton', qty: 0, days: 0, rate: 0, subtotal: 0 },
              { name: 'Broderson _____ ton', qty: 0, days: 0, rate: 0, subtotal: 0 },
              { name: 'Articulating Lift', qty: 0, days: 0, rate: 0, subtotal: 0 },
              { name: 'Scissor Lift', qty: 0, days: 0, rate: 0, subtotal: 0 }
            ],
            
            // Consumables
            consumables_used: [
              // Gases
              { name: 'Propane (33lbs)', qty: 0, rate: 25.00, subtotal: 0 },
              { name: 'Oxygen', qty: 0, rate: 30.00, subtotal: 0 },
              { name: 'Acetylene', qty: 0, rate: 35.00, subtotal: 0 },
              { name: 'Argon (Small)', qty: 0, rate: 40.00, subtotal: 0 },
              { name: 'Argon (Large)', qty: 0, rate: 60.00, subtotal: 0 },
              { name: 'Nitrogen', qty: 0, rate: 35.00, subtotal: 0 },
              
              // Fuel & Transportation
              { name: 'Fuel Surcharge', qty: 0, rate: 15.00, subtotal: 0 },
              { name: '407 ETR', qty: 0, rate: 0, subtotal: 0 },
              { name: 'Diesel Fuel', qty: 0, rate: 0, subtotal: 0 },
              { name: 'Gasoline', qty: 0, rate: 0, subtotal: 0 },
              
              // Welding Rods & Materials
              { name: '7018 x 1/8" (lbs)', qty: 0, rate: 8.00, subtotal: 0 },
              { name: '7018 x 3/32" (lbs)', qty: 0, rate: 8.00, subtotal: 0 },
              { name: '7018 x 5/32" (lbs)', qty: 0, rate: 8.00, subtotal: 0 },
              { name: 'Other Welding Rod', qty: 0, rate: 8.00, subtotal: 0 },
              
              // Cutting & Grinding Supplies
              { name: '5" Zipcut Discs', qty: 0, rate: 2.50, subtotal: 0 },
              { name: '6" Zipcut Discs', qty: 0, rate: 3.00, subtotal: 0 },
              { name: '5" Grinding Discs', qty: 0, rate: 2.00, subtotal: 0 },
              { name: '6" Grinding Discs', qty: 0, rate: 2.50, subtotal: 0 },
              { name: 'Sanding Discs', qty: 0, rate: 1.50, subtotal: 0 },
              { name: 'Polishing Discs', qty: 0, rate: 1.50, subtotal: 0 },
              { name: 'Deburr Bits', qty: 0, rate: 5.00, subtotal: 0 },
              
              // Safety Equipment
              { name: 'Welding Sleeves', qty: 0, rate: 20.00, subtotal: 0 },
              { name: 'Welding Gloves', qty: 0, rate: 15.00, subtotal: 0 },
              { name: 'TIG Gloves', qty: 0, rate: 18.00, subtotal: 0 },
              { name: 'Hand Heat Shields', qty: 0, rate: 12.00, subtotal: 0 },
              { name: 'Inside Lenses', qty: 0, rate: 3.00, subtotal: 0 },
              { name: 'Outside Lenses', qty: 0, rate: 2.00, subtotal: 0 },
              { name: '3M Organic Filters', qty: 0, rate: 8.00, subtotal: 0 },
              { name: 'Safety Glasses', qty: 0, rate: 5.00, subtotal: 0 },
              { name: 'Box Masks', qty: 0, rate: 25.00, subtotal: 0 },
              { name: 'Clear Face Shields', qty: 0, rate: 10.00, subtotal: 0 },
              
              // Hardware & Fasteners
              { name: 'Shim (4x4)', qty: 0, rate: 2.00, subtotal: 0 },
              { name: 'Anchors 3/8" x 4"', qty: 0, rate: 1.50, subtotal: 0 },
              { name: 'Anchors 1/2" x 5"', qty: 0, rate: 2.00, subtotal: 0 },
              { name: 'Chemical Anchors', qty: 0, rate: 5.00, subtotal: 0 },
              { name: 'Grout', qty: 0, rate: 0, subtotal: 0 },
              { name: 'Cutting Fluid', qty: 0, rate: 0, subtotal: 0 }
            ],
            
            // Purchases
            purchases: [],
            
            // Tools/Equipment (Yes/No tracking)
            tools_used: {
              company_vehicle: false,
              took_407: false,
              welder: false,
              trailer: false,
              cutting_torches: false,
              hammer_drill: false,
              track_jacks: false,
              skates: false,
              dollies: false,
              roto_hammer: false,
              hydraulic_rams: false,
              machine_skates: false,
              corner_dollies: false,
              magdrill: false,
              auto_level: false
            },
            
            // Signatures and Approval
            technician_signature: '',
            customer_signature: '',
            customer_approval_name: '',
            
            // Metadata
            created_at: new Date().toISOString(),
            offline_created: false,
            sync_status: 'pending'
          });
          setWorkOrderData(null);
          setScanResult('');
          setActiveTab('scanner'); // Return to scanner tab after successful submission
        } else {
          throw new Error(result.message || 'Submission failed');
        }
      } else {
        throw new Error('Network error');
      }
    } catch (error) {
      showNotification('error', `Failed to submit timesheet: ${error.message}`);
    }
  };

  const syncOfflineTimesheets = async () => {
    if (!isOnline) return;

    const offlineTimesheets = JSON.parse(localStorage.getItem('offlineTimesheets') || '[]');
    if (offlineTimesheets.length === 0) return;

    for (const timesheet of offlineTimesheets) {
      try {
        const response = await fetch(`${API}/mobile/timesheets`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(timesheet)
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            // Remove synced timesheet from offline storage
            const updatedOffline = offlineTimesheets.filter(t => t !== timesheet);
            localStorage.setItem('offlineTimesheets', JSON.stringify(updatedOffline));
          }
        }
      } catch (error) {
        console.error('Failed to sync timesheet:', error);
      }
    }

    const remainingOffline = JSON.parse(localStorage.getItem('offlineTimesheets') || '[]');
    if (remainingOffline.length < offlineTimesheets.length) {
      showNotification('success', 'Offline timesheets synced successfully!');
    }
  };

  // Sync offline timesheets when coming online
  useEffect(() => {
    if (isOnline) {
      syncOfflineTimesheets();
    }
  }, [isOnline]);

  return (
    <div className="mobile-timesheet-app">
      {/* Status Bar */}
      <div className="status-bar">
        <div className="connection-status">
          <span className={`status-indicator ${isOnline ? 'online' : 'offline'}`}></span>
          {isOnline ? 'Online' : 'Offline'}
        </div>
        <h1 className="app-title">365Ops Mobile</h1>
      </div>

      {/* Navigation Tabs */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'scanner' ? 'active' : ''}`}
          onClick={() => setActiveTab('scanner')}
        >
          <span className="tab-icon">📷</span>
          <span className="tab-label">Scan QR</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'generator' ? 'active' : ''}`}
          onClick={() => setActiveTab('generator')}
        >
          <span className="tab-icon">⚡</span>
          <span className="tab-label">Generate</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'timesheet' ? 'active' : ''}`}
          onClick={() => setActiveTab('timesheet')}
        >
          <span className="tab-icon">📋</span>
          <span className="tab-label">Timesheet</span>
        </button>
      </div>

      {/* Content Area */}
      <div className="content-area">
        {activeTab === 'scanner' && (
          <div className="scanner-section">
            <MobileQRScanner
              onScan={handleScan}
              onError={handleScanError}
              isActive={activeTab === 'scanner'}
            />
            
            {scanResult && (
              <div className="scan-result">
                <h3>Scanned Content:</h3>
                <div className="result-content">{scanResult}</div>
                <div className="result-actions">
                  <button
                    onClick={() => navigator.clipboard.writeText(scanResult)}
                    className="action-button"
                  >
                    📋 Copy
                  </button>
                  <button
                    onClick={() => setScanResult('')}
                    className="action-button"
                  >
                    🗑️ Clear
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'generator' && (
          <MobileQRGenerator onGenerate={handleGenerate} />
        )}

        {activeTab === 'timesheet' && (
          <ComprehensiveTimesheet
            initialData={timesheetData}
            onSave={handleTimesheetSubmit}
            onCancel={() => setActiveTab('scanner')}
          />
        )}
      </div>

      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notification => (
          <div
            key={notification.id}
            className={`notification ${notification.type}`}
          >
            <span className="notification-icon">
              {notification.type === 'success' ? '✅' : 
               notification.type === 'error' ? '❌' : 
               notification.type === 'info' ? 'ℹ️' : '⚠️'}
            </span>
            <span className="notification-message">{notification.message}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MobileTimesheetApp;